import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import Fetching from '../Fetching';
import CartLineItem from './CartLineItem';
import CouponForm from './CouponForm';
import LineItemsRemoved from './LineItemsRemoved';
import FormattedNumber from '../FormattedNumber';
import dataAids from '../../../constants/dataAids';
import {
  OlsConfigStore,
  CartStore,
  CartActions
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import {
  ApplePayActions,
  DigitalWalletStore,
  PaymentRequestActions,
  PreloadResourceStore,
  PreloadResourceActions,
  CookieBannerUtils,
  PoyntPaymentRequestActions
} from '../../utils/ols-core';
import MembershipUtils from '../../utils/ols-core/MembershipUtils';
import { ErrorMessage } from '../lazy';
import PixelTracker from '../../utils/PixelTracker';
import { assign } from 'lodash';
import * as Metrics from '../utils/trackMetric';
import * as MetricEvents from '../../../constants/metrics';
import { mapContextToProps } from '../OlsShopContext';
import GoogleAnalytics from '../../utils/GoogleAnalytics';
import { clickIdTracker } from '../../utils/TrackingUtils';
import CartButtons from './CartButtons';

export class CartListV2 extends React.Component {
  constructor() {
    super(...arguments);
    this.state = assign(
      {
        hasLoggedGoogleImpression: false,
        showMobileCoupon: false,
        matchingButtonId: null
      },
      this.getStoreState()
    );
  }

  getStoreState() {
    const { fetching, processing, error, errorRetryable, cart, validCoupon } = CartStore.getState();
    const { resourcesPreloaded } = PreloadResourceStore.getState();
    const { inSession } = DigitalWalletStore.getState();
    const cartHasItems = cart ? cart.total_quantity > 0 : false;

    return {
      fetching,
      processing,
      inSession,
      error,
      errorRetryable,
      cartHasItems,
      cart,
      validCoupon,
      showCoupon: OlsConfigStore.getState().show_coupon,
      modifiedLineItems: {}, // { id: { quantity: ..., etc. } }
      applePayIsAvailable: this.checkApplePayAvailability(),
      resourcesPreloaded,
      isModified: false
    };
  }

  checkApplePayAvailability() {
    const { stripe_config, poynt_config } = OlsConfigStore.getState();
    const { stripeWasLoaded, poyntWasLoaded, applePayIsAvailable, googlePayIsAvailable } =
      DigitalWalletStore.getState();

    if (poyntWasLoaded) {
      return (applePayIsAvailable || googlePayIsAvailable) && poynt_config.with_apple_pay;
    }

    if (stripeWasLoaded) {
      return applePayIsAvailable && stripe_config?.apple_pay_on_product_detail;
    }

    return false;
  }

  initPaymentRequest() {
    const { stripeWasLoaded, poyntWasLoaded } = DigitalWalletStore.getState();
    const { configWasLoaded, stripe_config, poynt_config } = OlsConfigStore.getState();

    if (stripeWasLoaded || poyntWasLoaded || !configWasLoaded) return;

    if (poynt_config.with_apple_pay) {
      PoyntPaymentRequestActions.init();
    } else if (stripe_config.with_apple_pay) {
      if (stripe_config.with_payment_request) {
        PaymentRequestActions.init();
      } else {
        const staticContent = this.props.staticContent || {};
        ApplePayActions.init(staticContent.apple_pay);
      }
    }
  }

  UNSAFE_componentWillMount() {
    CartStore.addListener('change', this.onStoreChange);
    OlsConfigStore.addListener('change', this.onOlsConfigChange);
    OlsConfigStore.addListener('change', this.onStoreChange);
    DigitalWalletStore.addListener('change', this.onDigitalWalletChange);
    CartStore.addListener('change', this.onDigitalWalletChange);
    PreloadResourceStore.addListener('change', this.onStoreChange);
    // Workaround to accommodate a delayed call to CartActions.loadCart()
    this.setState({ fetching: true });
    this.initPaymentRequest();
  }

  async componentDidUpdate(prevProps) {
    const { freeShippingLabelEnabled } = OlsConfigStore.getState();
    const { renderMode, freeShippingText } = this.props;
    const isModified = this.determineIfModified();

    if (isModified !== this.state.isModified) {
      this.setState({
        isModified
      });
    }

    if (!this.state.resourcesPreloaded && renderMode === 'PUBLISH') {
      PreloadResourceActions.preloadResources();
    }

    if (
      freeShippingText &&
      prevProps.freeShippingText !== freeShippingText &&
      freeShippingLabelEnabled
    ) {
      Metrics.track(MetricEvents.CART_FREE_SHIPPING_LABEL_IMPRESSION);
    }
  }

  componentWillUnmount() {
    CartStore.removeListener('change', this.onStoreChange);
    OlsConfigStore.removeListener('change', this.onOlsConfigChange);
    OlsConfigStore.removeListener('change', this.onStoreChange);
    DigitalWalletStore.removeListener('change', this.onDigitalWalletChange);
    CartStore.removeListener('change', this.onDigitalWalletChange);
    PreloadResourceStore.removeListener('change', this.onStoreChange);
  }

  onStoreChange = () => {
    const { hasLoggedGoogleImpression } = this.state;
    const { fetching, processing, cart } = CartStore.getState();

    this.setState(this.getStoreState());

    if (!cart) return;

    if (!fetching && !processing && !hasLoggedGoogleImpression) {
      GoogleAnalytics.logViewCart();
      this.setState({ hasLoggedGoogleImpression: true });
    }
  };

  onOlsConfigChange = () => {
    this.initPaymentRequest();
  };

  onDigitalWalletChange = () => {
    const { poyntWasLoaded, stripeWasLoaded, availabilityWasChecked, inSession } =
      DigitalWalletStore.getState();
    const { poynt_config, stripe_config } = OlsConfigStore.getState();

    if (poyntWasLoaded && poynt_config.with_apple_pay) {
      if (availabilityWasChecked) {
        this.setState({
          inSession,
          applePayIsAvailable: this.checkApplePayAvailability()
        });
      } else {
        setTimeout(() => PoyntPaymentRequestActions.checkAvailability(), 0);
      }
    } else if (stripeWasLoaded) {
      if (availabilityWasChecked) {
        this.setState({
          inSession,
          applePayIsAvailable: this.checkApplePayAvailability()
        });
      } else {
        setTimeout(() => {
          if (stripe_config.with_payment_request) {
            PaymentRequestActions.checkAvailability();
          } else {
            ApplePayActions.checkAvailability();
          }
        }, 0);
      }
    }
  };

  componentDidMount() {
    this.setState({ matchingButtonId: '#checkout-btn' });
    // TODO Research alternative workaround
    setTimeout(() => {
      // Pass true to destroy expired line items
      // and message the user on this page.
      CartActions.loadCart(true);
    }, 0);

    const { cart } = CartStore.getState();
    const { loggedIn } = MembershipUtils;
    const cartSize = cart && cart.line_items ? cart.line_items.length : 0;

    Metrics.track(MetricEvents.CART_VIEW_IMPRESSION({ loggedIn, cartSize }));
  }

  handleModifyLineItem = (id, e) => {
    let quantity = +e.target.value;
    const { modifiedLineItems } = this.state;

    if (quantity < 0) {
      quantity = 0;
    } else if (quantity > 999) {
      quantity = 999;
    }

    if (!modifiedLineItems[id]) {
      modifiedLineItems[id] = {};
    }

    const beginningQty = this.state.cart.line_items.find(ln => ln.id === id).quantity;

    Metrics.track(
      MetricEvents.CART_ADJUST_ITEM_QTY_CLICK({
        adjustedQty: quantity,
        beginningQty
      })
    );

    modifiedLineItems[id].quantity = quantity;

    this.setState({
      modifiedLineItems: modifiedLineItems
    });
  };

  handleUpdateClick = () => {
    const lineItemAttributes = this.getModifiedLineItems();

    Metrics.track(MetricEvents.CART_UPDATE_CLICK);

    CartActions.modifyCart(lineItemAttributes);
  };

  getModifiedLineItems = () => {
    const { modifiedLineItems } = this.state;
    const lineItemAttributes = [];

    // Populate lineItemAttributes
    for (const id in modifiedLineItems) {
      if (modifiedLineItems.hasOwnProperty(id)) {
        const lineItem = {
          id: id,
          quantity: modifiedLineItems[id].quantity
        };
        lineItemAttributes.push(lineItem);
      }
    }

    return lineItemAttributes;
  };

  handleApplePayClick = event => {
    event.preventDefault();
    ApplePayActions.startSession({ order: this.state.cart, init_page: 'cart' });
    Metrics.track(MetricEvents.CART_APPLE_PAY_BUTTON_CLICK);
  };

  handleCheckoutClick = event => {
    event.preventDefault();
    let lineItemAttributes = this.getModifiedLineItems();
    if (lineItemAttributes.length < 1) {
      lineItemAttributes = this.state.cart.line_items;
    }

    Metrics.track(MetricEvents.CART_CHECKOUT_CLICK);
    PixelTracker.logInitiateCheckout();
    CartActions.doCheckout(lineItemAttributes);
    GoogleAnalytics.logBeginCheckout();
  };

  getOrderData = () => {
    return { order: this.state.cart };
  };

  showCouponForm = () => {
    this.setState({ showMobileCoupon: true });
  };

  determineIfModified = () => {
    const { modifiedLineItems, cart } = this.state;
    const lineItems = cart && cart.line_items ? cart.line_items : [];

    const isModified = lineItems.some(li => {
      const modifiedLi = modifiedLineItems[li.id];
      return modifiedLi && +li.quantity !== +modifiedLi.quantity;
    });

    return isModified;
  };

  headerData() {
    const { freeShippingLabelEnabled } = OlsConfigStore.getState();
    const { freeShippingText } = this.props;
    const translate = this.props.translate;
    const { Block, Label } = UX2.Element;
    const {
      Grid,
      Grid: { Cell }
    } = UX2.Component;
    const styles = {
      headerContainer: {
        'paddingBottom': 'small',
        'borderBottomWidth': '2px',
        'borderBottomStyle': 'solid',
        'borderColor': 'section',
        '@xs-only': {
          border: 'none'
        }
      },
      headerRight: {
        '@xs-only': {
          display: 'none'
        }
      },
      totalCell: {
        '@sm': {
          textAlign: 'right'
        }
      },
      freeShipping: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      },
      freeShippingContent: {
        color: 'primary'
      }
    };

    return (
      <Block style={ styles.headerContainer }>
        <Grid size={ 24 }>
          <Cell xs={ 24 } sm={ 11 } md={ 13 } style={ styles.freeShipping }>
            { freeShippingText && freeShippingLabelEnabled ? (
              <Label.Minor style={ styles.freeShippingContent } children={ freeShippingText } />
            ) : null }
          </Cell>
          <Cell sm={ 13 } md={ 11 } style={ styles.headerRight }>
            <Grid size={ 12 } gridSm={ 24 }>
              <Cell sm={ 4 }>
                <Label.Minor children={ translate('cart.price') } />
              </Cell>
              <Cell sm={ 4 }>
                <Label.Minor children={ translate('cart.quantity') } />
              </Cell>
              <Cell sm={ 4 } style={ styles.totalCell }>
                <Label.Minor children={ translate('cart.total') } />
              </Cell>
            </Grid>
          </Cell>
        </Grid>
      </Block>
    );
  }

  renderGeneralWarning() {
    const { error } = this.state;
    const { Block } = UX2.Element;
    if (error && error.messages) {
      const errorMessages = error.messages.general_error;
      const styles = {
        marginBottom: 'medium',
        textAlign: 'center',
        fontColor: 'error'
      };
      if (errorMessages) {
        return <Block children={ errorMessages } style={ styles } />;
      }
    }
    return null;
  }

  renderFooter(cart) {
    const { translate } = this.props;
    const { item_total, promo_total, coupon_code, item_subtotal_with_discount } = cart;
    const { Block, Link, Label } = UX2.Element;
    const { poynt_config } = OlsConfigStore.getState();
    const { showCoupon, showMobileCoupon, validCoupon } = this.state;
    const hasCoupon = showCoupon && promo_total !== '0.0';
    const showPoyntPaymentButtons = this.state.applePayIsAvailable && poynt_config.with_apple_pay;

    let couponLabel = translate('cart.coupon');
    if (coupon_code) {
      couponLabel += ` (${coupon_code})`;
    }

    const styles = {
      checkoutButtonWithApple: {
        '@xs': {
          borderRadius: '5px',
          paddingLeft: 'xsmall',
          paddingRight: 'xsmall',
          paddingTop: 'small',
          paddingBottom: 'small',
          lineHeight: '18px',
          width: '100%'
        },
        '@sm': {
          paddingLeft: 'medium',
          paddingRight: 'medium'
        }
      },
      updateCartMobile: {
        'display': 'flex',
        'justifyContent': 'flex-end',
        'marginTop': 'medium',
        'marginBottom': 'small',
        '@sm': {
          display: 'none'
        }
      },
      divider: {
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderColor: 'section'
      },
      couponLinkMobile: {
        'display': 'flex',
        'justifyContent': 'flex-end',
        'marginTop': 'small',
        '@sm': {
          display: 'none'
        }
      },
      couponFormMobile: {
        'display': 'flex',
        'marginTop': 'small',
        'paddingBottom': 'small',
        'alignItems': 'center',
        'justifyContent': 'flex-start',
        'borderBottomWidth': '1px',
        'borderBottomStyle': 'solid',
        'borderColor': 'section',
        '@sm': {
          display: 'none'
        }
      },
      efficientCheckoutWrapper: {
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': showCoupon ? 'space-between' : 'flex-end',
        'marginTop': 'medium',
        '@xs-only': {
          justifyContent: 'flex-end'
        }
      },
      subtotalLabelWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 'small'
      },
      subtotalLabel: {
        marginRight: 'medium',
        marginBottom: '0'
      },
      subtotalNumber: {
        marginVertical: '0'
      },
      subtotalCommonLabelWrapper: {
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'flex-end',
        'marginTop': 'xsmall',
        '@sm': {
          marginTop: 'xxsmall'
        }
      },
      subtotalCommonLabel: {
        marginRight: 'medium'
      },
      couponFormDesktop: {
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'flex-end',
        '@sm': {
          justifyContent: showPoyntPaymentButtons ? 'flex-end' : 'flex-start'
        }
      },
      poyntButtonsContainer: {
        'display': 'flex',
        'flexDirection': 'row',
        'alignItems': 'center',
        'justifyContent': 'flex-end',
        '@xs-only': {
          flexDirection: 'column',
          width: '100%'
        }
      },
      checkoutButton: {
        'marginLeft': 'small',
        '@xs-only': {
          marginLeft: '0',
          width: '100% !important', // Needed for width to take precedence over theme overrides on mobile
          marginTop: showPoyntPaymentButtons ? 'xsmall' : '0'
        }
      },
      poyntButtons: {
        'marginLeft': 'small',
        '@xs-only': {
          marginLeft: '0',
          width: '100%'
        }
      },
      stripeButtons: {
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'flex-end',
        '@xs-only': {
          flexDirection: 'column-reverse',
          width: '100%',
          gap: 'small'
        }
      }
    };

    const updateCart = (
      <Block>
        <Link
          onClick={ this.state.isModified ? this.handleUpdateClick : '' }
          href='javascript:;'
          type='submit'
          data-aid={ dataAids.CART_UPDATE }
          children={ translate('cart.update') }
          disabled={ !this.state.isModified }
        />
      </Block>
    );

    return (
      <Block>
        <Block style={{ marginBottom: 'small' }}>
          <Block children={ updateCart } style={ styles.updateCartMobile } />
          <Block style={ styles.divider } />
          { showCoupon && !showMobileCoupon && (
            <Block style={ styles.couponLinkMobile }>
              <Link
                onClick={ this.showCouponForm }
                href='javascript:;'
                children={ translate('coupon.mobileCouponCode') }
                type='submit'
                style={{
                  fontSize: 'medium'
                }}
              />
            </Block>
          ) }
          { showCoupon && showMobileCoupon && (
            <Block style={ styles.couponFormMobile }>
              <CouponForm
                data-aid={ dataAids.COUPON_MOBILE_FORM }
                validCoupon={ validCoupon }
                fetching={ this.showFetching }
              />
            </Block>
          ) }
          <Block style={ styles.efficientCheckoutWrapper }>
            { showCoupon && (
              <Block
                style={{
                  '@xs-only': {
                    display: 'none'
                  }
                }}
              >
                <Block style={ styles.couponFormDesktop }>
                  <CouponForm
                    data-aid={ dataAids.COUPON_DESKTOP_FORM }
                    validCoupon={ validCoupon }
                    fetching={ this.showFetching }
                  />
                </Block>
              </Block>
            ) }
            <Block style={ styles.subtotalLabelWrapper }>
              <Block
                style={{
                  '@xs-only': {
                    display: 'none'
                  }
                }}
              >
                { updateCart }
              </Block>
              <Label.Minor children={ translate('cart.subtotal') } style={ styles.subtotalLabel } />
              <FormattedNumber
                number={ +item_total }
                currency={ true }
                data-aid={ dataAids.CART_SUB_TOTAL }
                style={ styles.subtotalNumber }
              />
            </Block>
          </Block>
          { hasCoupon && (
            <Block>
              <Block style={ styles.subtotalCommonLabelWrapper }>
                <Label.Minor children={ couponLabel } style={ styles.subtotalCommonLabel } />
                <FormattedNumber number={ +promo_total } currency style={ styles.subtotalNumber } />
              </Block>
              <Block style={ styles.subtotalCommonLabelWrapper }>
                <Label.Minor
                  children={ translate('cart.est_total') }
                  style={ styles.subtotalCommonLabel }
                />
                <FormattedNumber
                  number={ +item_subtotal_with_discount }
                  currency
                  style={ styles.subtotalNumber }
                />
              </Block>
            </Block>
          ) }
        </Block>
        { this.renderGeneralWarning() }
      </Block>
    );
  }

  renderTopCheckoutButtons(cart) {
    const { renderMode, category, shopHeader } = this.props;
    const { stripe_config, poynt_config } = OlsConfigStore.getState();
    const orderTotal = cart?.item_total || 0;
    const showApplePayButton = this.state.applePayIsAvailable && orderTotal > 0.0;
    const { matchingButtonId } = this.state;
    const el = document.querySelectorAll('#continue-shopping-breadcrumb');

    if (el && el.length > 0) {
      el[0].style.display = 'none';
    }

    const checkout = (
      <UX2.Element.Button.Primary
        id='checkout-btn'
        width='full'
        state={ renderMode === 'PUBLISH' && !this.showFetching ? 'default' : 'disabled' }
        onClick={ this.handleCheckoutClick }
        data-aid={ dataAids.CART_CHECKOUT }
        children='Checkout'
      />
    );

    return (
      <UX2.Element.Block
        id='cart-buttons-top'
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'large'
        }}
      >
        { shopHeader }
        <UX2.Element.Block>
          <CartButtons
            renderMode={ renderMode }
            category={ category }
            checkout={ checkout }
            showApplePayButton={ showApplePayButton }
            stripe_config={ stripe_config }
            poynt_config={ poynt_config }
            showPoyntPaymentButtons={ this.state.applePayIsAvailable && poynt_config.with_apple_pay }
            matchingButtonId={ matchingButtonId }
            getOrderData={ this.getOrderData }
            disabled={ renderMode !== 'PUBLISH' || this.showFetching }
            phoneRequired={ cart.require_phone_number }
          />
        </UX2.Element.Block>
      </UX2.Element.Block>
    );
  }

  addTranslateHash = () => {
    /* eslint-disable no-undef */
    const hasCookie = document.cookie && document.cookie.indexOf('googtrans') > -1;
    if (
      hasCookie &&
      typeof google === 'object' &&
      google.translate &&
      google.translate.TranslateElement
    ) {
      const translateInstance = google.translate.TranslateElement();
      const lang = translateInstance && translateInstance.c;
      return `#googtrans(${lang})`;
    }
    /* eslint-enable no-undef */

    return '';
  };

  get showFetching() {
    return this.state.fetching || this.state.processing || this.state.inSession;
  }

  get nonce() {
    const { cart } = this.state;
    return cart && cart.nonce;
  }

  get checkoutUrl() {
    let endpoint = 'checkout';
    if (!this.nonce) {
      endpoint = 'vnext-checkout';
    }
    return `${
      OlsConfigStore.getState().olsPublishedSiteHost
    }/${endpoint}${this.addTranslateHash()}`;
  }

  render() {
    const translate = this.props.translate;
    const { inSession, cartHasItems, cart, error } = this.state;
    const { Block, Input } = UX2.Element;
    const { Form } = UX2.Group;
    const {
      Grid,
      Grid: { Cell }
    } = UX2.Component;
    const styles = {
      contentsData: {
        marginTop: 'large',
        fontSize: 'large',
        textAlign: 'center',
        fontColor: 'section'
      }
    };
    let headerData = null;
    let lineItemData;
    let loadingState = true;
    let contentsData = null;
    let orderData = null;
    if (error && error.status_code === 404) {
      lineItemData = <ErrorMessage message={ translate('cart.error_fetching_order') } />;
    } else if (cart) {
      if (cartHasItems) {
        loadingState = false;
        headerData = this.headerData();
        const totalItems = cart.line_items.length;
        lineItemData = cart.line_items.map((lineItem, i) => {
          const modifiedLineItems = this.state.modifiedLineItems || {};
          const modifiedLineItem = modifiedLineItems[lineItem.id];
          const quantity = modifiedLineItem ? modifiedLineItem.quantity : lineItem.quantity;
          const lastItem = totalItems === i + 1;
          let relevantError = null; // String

          if (error && error.status_code === 422) {
            const { errors } = JSON.parse(error.message);
            relevantError = errors[lineItem.id];
          }

          return (
            <CartLineItem
              lineItem={ lineItem }
              lastItem={ lastItem }
              quantity={ quantity }
              onChange={ this.handleModifyLineItem }
              error={ relevantError }
              key={ 'cart_line_item_' + lineItem.id }
            />
          );
        });

        orderData = (
          <Block>
            { this.nonce ? (
              <Input type='hidden' name='order_nonce' value={ this.nonce } />
            ) : (
              <Fragment>
                <Input
                  type='hidden'
                  name='order_number'
                  value={ CartStore.getState().order.number }
                />
                <Input type='hidden' name='order_token' value={ CartStore.getState().order.token } />
              </Fragment>
            ) }
          </Block>
        );

        contentsData = (
          <Grid size={ 12 }>
            <Cell xs={ 12 } sm={ 10 } pushSm={ 1 }>
              { this.renderTopCheckoutButtons(cart) }
              <Form id={ dataAids.DO_CHECKOUT_ID } action={ this.checkoutUrl } method='POST'>
                { headerData }
                { lineItemData }
                { orderData }
                <Input
                  type='hidden'
                  name='cookie_terms_accepted'
                  value={ CookieBannerUtils.isAccepted }
                />
                <Input type='hidden' name='gdan_clid' value={ clickIdTracker.getGdanClidValue() } />
                <Input type='hidden' name='gclid' value={ clickIdTracker.getGoogleClickId() } />
                <Input type='hidden' name='_fbc' value={ clickIdTracker.getFacebookClickId() } />
                <Input type='hidden' name='_fbp' value={ clickIdTracker.getFacebookBrowserId() } />
                { this.renderFooter(cart) }
              </Form>
            </Cell>
          </Grid>
        );
      } else {
        contentsData = (
          <Block
            data-aid={ dataAids.CART_EMPTY_TEXT }
            children={ translate('cart.empty_cart') }
            style={ styles.contentsData }
          />
        );
      }
    } else {
      contentsData = <Block children={ translate('cart.no_cart') } style={ styles.contentsData } />;
    }

    return (
      <Block position='relative'>
        <LineItemsRemoved lineItems={ cart && cart.line_item_products_removed } />
        { contentsData }
        <Fetching
          fetching={ this.showFetching }
          pageTransition={ loadingState }
          transparentBackground={ inSession }
        />
      </Block>
    );
  }
}

CartListV2.propTypes = {
  staticContent: PropTypes.object,
  renderMode: PropTypes.string,
  category: PropTypes.string,
  translate: PropTypes.func,
  featureFlags: PropTypes.object,
  freeShippingText: PropTypes.string,
  shopHeader: PropTypes.object
};

CartListV2.defaultProps = {
  translate: () => {}
};

export default mapContextToProps(CartListV2, (context, ownProps) => ({
  ...ownProps,
  translate: context.translate,
  featureFlags: context.featureFlags,
  freeShippingText: context.freeShippingText
}));
